import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import { colors } from "../../../../theme";
import { parseIpTransitions } from "./assetUtils";

// `ip_history[]` holds TRANSITIONS — { old_ip, new_ip, changed_at, source } — not a
// list of addresses each held for a period. Rendering them as "A → B, on this date,
// caught by this signal" is the only shape that matches the data; the old
// from/to-period rendering silently produced empty rows against it.
//
// `source` is the signal that detected the change, and its strength is part of the
// fact: arp/dhcp/conn/eui64 were observed, `snapshot` was inferred by diffing
// against the previous state. A source naming the engine (orion/scapy) is not a
// signal at all and means the entry predates the current document shape.
const IpHistoryDialog = ({ row, onClose }) => {
  const transitions = parseIpTransitions(row?.ip_history);

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
        <HistoryIcon sx={{ color: "#7b8bff", fontSize: 22 }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>Address Changes</Typography>
          <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 0.2 }}>
            Currently at <span style={{ fontFamily: "monospace", color: "#7b8bff" }}>{row?.ip}</span>
          </Typography>
        </Box>
        <Chip
          label={`${transitions.length} change${transitions.length === 1 ? "" : "s"}`}
          size="small"
          sx={{ ml: "auto", bgcolor: "#4254fb22", color: "#7b8bff", fontSize: 11, fontWeight: 700 }}
        />
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
        {transitions.length === 0 && (
          <Typography sx={{ color: colors.grey[500], fontSize: 13, fontStyle: "italic" }}>
            This asset has not changed address since it was first seen.
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {transitions.map((t, i) => (
            <Box
              key={i}
              sx={{
                borderRadius: 1,
                backgroundColor: "#222",
                border: `1px solid ${colors.grey[800]}`,
                borderLeft: `3px solid ${t.signal?.strong ? "#4cceac" : "#ffb422"}`,
                p: 1.2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: "monospace", fontSize: 13.5, color: colors.grey[400] }}>
                  {t.old_ip ?? <span style={{ fontStyle: "italic" }}>unknown</span>}
                </Typography>
                <ArrowForwardIcon sx={{ fontSize: 15, color: colors.grey[600] }} />
                <Typography sx={{ fontFamily: "monospace", fontSize: 13.5, fontWeight: 700, color: "#7b8bff" }}>
                  {t.new_ip ?? <span style={{ fontStyle: "italic" }}>unknown</span>}
                </Typography>
                {t.changed_at && (
                  <Typography sx={{ fontSize: 12, color: colors.grey[300], ml: "auto" }}>
                    {dayjs(t.changed_at).format("YYYY-MM-DD HH:mm")}
                  </Typography>
                )}
              </Box>

              {t.signal && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.7 }}>
                  <Tooltip title={t.signal.note}>
                    <Chip
                      icon={t.signal.stale ? <WarningAmberIcon sx={{ fontSize: "12px !important" }} /> : undefined}
                      label={t.signal.label}
                      size="small"
                      sx={{
                        bgcolor: t.signal.strong ? "#4cceac22" : "#ffb42222",
                        color: t.signal.strong ? "#4cceac" : "#ffb422",
                        fontSize: 10, height: 18, fontWeight: 700,
                        "& .MuiChip-icon": { color: "#ffb422" },
                      }}
                    />
                  </Tooltip>
                  <Typography sx={{ fontSize: 11, color: colors.grey[500] }}>{t.signal.note}</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}` }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IpHistoryDialog;
